/**
 * @fileoverview Inline message component for contextual feedback
 * @module ctl-inline-message
 * @version 1.0.0
 * @deprecated Use ctl-message with variant="inline" instead. This component is maintained for backwards compatibility.
 * 
 * @description
 * An inline message component that flows with surrounding text.
 * 
 * **DEPRECATED**: This component has been replaced by the unified `ctl-message` component.
 * Please use `<ctl-message variant="inline">` instead. This component is maintained for backwards compatibility.
 * 
 * @example
 * <!-- DEPRECATED: Old way -->
 * <ctl-inline-message tone="danger" size="sm">Required field</ctl-inline-message>
 * 
 * <!-- RECOMMENDED: New way -->
 * <ctl-message variant="inline" tone="danger" size="sm">Required field</ctl-message>
 */

import './ctl-message.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-message-text: var(--ctl-color-text, #111827);

    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-1-5);
    color: var(--ctl-message-text);
    font-family: var(--ctl-font-sans, system-ui);
    line-height: var(--ctl-leading-normal);
  }

  /* Size variants */
  :host([size="sm"]) {
    font-size: var(--ctl-font-size-sm);
  }

  :host([size="md"]) {
    font-size: var(--ctl-font-size-md);
  }

  /* Tone colors - neutral (default) */
  :host(:not([tone]), [tone="neutral"]) {
    --ctl-message-text: var(--ctl-color-muted, #6b7280);
  }

  /* Tone colors - info */
  :host([tone="info"]) {
    --ctl-message-text: color-mix(in srgb, var(--ctl-color-info, #2563eb) 86%, var(--ctl-color-text, #111827));
  }

  /* Tone colors - success */
  :host([tone="success"]) {
    --ctl-message-text: color-mix(in srgb, var(--ctl-color-success, #16a34a) 86%, var(--ctl-color-text, #111827));
  }

  /* Tone colors - warning */
  :host([tone="warning"]) {
    --ctl-message-text: color-mix(in srgb, var(--ctl-color-warning, #d97706) 86%, var(--ctl-color-text, #111827));
  }

  /* Tone colors - danger */
  :host([tone="danger"]) {
    --ctl-message-text: color-mix(in srgb, var(--ctl-color-danger, #dc2626) 86%, var(--ctl-color-text, #111827));
  }

  .message-content {
    display: inline;
  }
</style>

<span class="message-content" part="content">
  <slot></slot>
</span>
`;

export class CtlInlineMessage extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
  }

  static get observedAttributes() {
    return ["tone", "size"];
  }

  connectedCallback() {
    // Set defaults
    if (!this.hasAttribute("tone")) this.setAttribute("tone", "neutral");
    if (!this.hasAttribute("size")) this.setAttribute("size", "sm");
  }
}

customElements.define("ctl-inline-message", CtlInlineMessage);
